import { useState } from 'react';
import type { JSX } from 'react';

import { SectionBlock } from 'shared/ui';
import type { SnackbarVariant } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import {
  useCreateProcessMutation,
  useDryRunProcessMutation,
  useRunProcessMutation,
  useUpdateProcessMutation,
} from '../api/integrations';
import { formatDate, formatJson } from '../lib/json';
import { PROVIDER_CATALOG, getProcessKind } from '../model/integration';
import type { IntegrationDetail, IntegrationProcess, LastRunResult } from '../model/integration';
import FormDialog, { Field } from './FormDialog';

const TRIGGER_LABELS: Record<string, string> = { tick: 'programada', manual: 'manual', webhook: 'webhook' };

// Dry runs and real runs share lastResult, so the summary says first which one it was
const LastResultSummary = ({ result }: { result: LastRunResult }): JSX.Element => {
  const stats = Object.entries(result.stats ?? {});
  return (
    <div className="flex flex-col gap-1 text-xs">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={result.dryRun ? 'outline' : 'secondary'}>{result.dryRun ? 'Dry run' : 'Real'}</Badge>
        {result.status ? (
          <span className={cn('font-medium', result.status === 'error' ? 'text-error' : 'text-success')}>
            {result.status === 'error' ? 'Error' : 'OK'}
          </span>
        ) : null}
        <span className="text-text-muted">
          {formatDate(result.finishedAt ?? null)}
          {result.triggerType ? ` · ${TRIGGER_LABELS[result.triggerType] ?? result.triggerType}` : ''}
          {result.durationMs !== undefined ? ` · ${(result.durationMs / 1000).toFixed(1)} s` : ''}
        </span>
      </div>
      {stats.length > 0 ? (
        <span className="font-mono text-[11px] text-text-default">
          {stats.map(([key, value]) => `${key}: ${value}`).join(' · ')}
        </span>
      ) : null}
      {result.error ? <span className="break-words text-error">{result.error}</span> : null}
      <details>
        <summary className="cursor-pointer text-text-muted">JSON completo</summary>
        <pre className="mt-1 max-h-60 overflow-auto rounded bg-bg-surface-raised p-2 text-[11px]">
          {formatJson(result)}
        </pre>
      </details>
    </div>
  );
};

const CRON_HINT =
  'Cron de 5 campos, en la zona horaria del proyecto (ej. */15 * * * *). Vacío = sin ejecución automática.';

type DialogState =
  | { mode: 'create' }
  | { mode: 'cron'; process: IntegrationProcess }
  | { mode: 'run'; process: IntegrationProcess };

interface ProcessDialogProps {
  integration: IntegrationDetail;
  state: DialogState;
  onClose: () => void;
  notify: (message: string, variant?: SnackbarVariant) => void;
  onRunRequested: () => void;
}

const ProcessDialog = ({ integration, state, onClose, notify, onRunRequested }: ProcessDialogProps): JSX.Element => {
  const processTypes = Object.keys(PROVIDER_CATALOG[integration.provider]?.processTypes ?? {});
  const [type, setType] = useState(processTypes[0] ?? '');
  const [cron, setCron] = useState(state.mode === 'cron' ? state.process.cron ?? '' : '');
  const [error, setError] = useState<string | null>(null);

  const [createProcess, { isLoading: isCreating }] = useCreateProcessMutation();
  const [updateProcess, { isLoading: isUpdating }] = useUpdateProcessMutation();
  const [runProcess, { isLoading: isRunning }] = useRunProcessMutation();

  const integrationId = integration.id;
  const createKind = getProcessKind(integration.provider, type);

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    try {
      if (state.mode === 'create') {
        await createProcess({
          integrationId,
          type,
          cron: createKind === 'scheduled' ? cron.trim() || null : null,
        }).unwrap();
        notify('Proceso creado (deshabilitado)');
      } else if (state.mode === 'cron') {
        await updateProcess({ id: state.process.id, integrationId, body: { cron: cron.trim() || null } }).unwrap();
        notify('Cron actualizado');
      } else {
        await runProcess({ id: state.process.id, integrationId }).unwrap();
        notify('Ejecución encolada. El resultado aparece en la tabla al terminar.');
        onRunRequested();
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo guardar el proceso'));
    }
  };

  const isSaving = isCreating || isUpdating || isRunning;

  if (state.mode === 'run') {
    return (
      <FormDialog
        title={`Ejecutar ${state.process.type}`}
        description="Corre de verdad: escribe en Lidz y en el sistema externo, igual que la ejecución programada."
        submitLabel="Ejecutar"
        destructive
        isSaving={isSaving}
        error={error}
        onClose={onClose}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <FormDialog
      title={state.mode === 'create' ? 'Nuevo proceso' : `Cron de ${state.process.type}`}
      description={
        state.mode === 'create'
          ? 'Se crea deshabilitado, para poder probarlo con un dry run antes de activarlo.'
          : undefined
      }
      submitLabel={state.mode === 'create' ? 'Crear' : 'Guardar'}
      isSaving={isSaving}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      {state.mode === 'create' ? (
        <Field label="Tipo" hint={createKind ? `Tipo de proceso: ${createKind}` : undefined}>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {processTypes.map((processType) => (
                <SelectItem key={processType} value={processType}>
                  {processType}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : null}
      {state.mode === 'cron' || createKind === 'scheduled' ? (
        <Field label="Cron" htmlFor="process-cron" hint={CRON_HINT}>
          <Input id="process-cron" value={cron} onChange={(e) => setCron(e.target.value)} className="font-mono" />
        </Field>
      ) : null}
    </FormDialog>
  );
};

interface ProcessesSectionProps {
  integration: IntegrationDetail;
  notify: (message: string, variant?: SnackbarVariant) => void;
  /** A run was enqueued: the page polls the detail until its result shows up */
  onRunRequested: () => void;
}

const ProcessesSection = ({ integration, notify, onRunRequested }: ProcessesSectionProps): JSX.Element => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [updateProcess] = useUpdateProcessMutation();
  const [dryRunProcess] = useDryRunProcessMutation();
  const [dryRunningId, setDryRunningId] = useState<number | null>(null);

  const toggleEnabled = async (process: IntegrationProcess, enabled: boolean): Promise<void> => {
    try {
      await updateProcess({ id: process.id, integrationId: integration.id, body: { enabled } }).unwrap();
      notify(enabled ? `${process.type} habilitado` : `${process.type} deshabilitado`);
    } catch (err) {
      notify(extractErrorMessage(err, 'No se pudo cambiar el estado'), 'error');
    }
  };

  const dryRun = async (process: IntegrationProcess): Promise<void> => {
    setDryRunningId(process.id);
    try {
      const { lastResult } = await dryRunProcess({ id: process.id, integrationId: integration.id }).unwrap();
      if (lastResult?.status === 'error') {
        notify(`Dry run de ${process.type} con error: ${lastResult.error ?? 'sin detalle'}`, 'error');
      } else {
        notify(`Dry run de ${process.type} terminado`);
      }
    } catch (err) {
      notify(extractErrorMessage(err, 'No se pudo correr el dry run'), 'error');
    } finally {
      setDryRunningId(null);
    }
  };

  const processes = [...integration.processes].sort((a, b) => a.type.localeCompare(b.type));

  return (
    <SectionBlock
      title={`Procesos (${processes.length})`}
      action={
        <Button size="sm" variant="outline" onClick={() => setDialog({ mode: 'create' })}>
          Agregar proceso
        </Button>
      }
    >
      {processes.length === 0 ? (
        <p className="m-0 text-sm text-text-muted">Sin procesos.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Activo</TableHead>
              <TableHead>Cron</TableHead>
              <TableHead>Ejecutar</TableHead>
              <TableHead>Último resultado</TableHead>
              <TableHead>Sincronizado hasta</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processes.map((process) => {
              const kind = getProcessKind(integration.provider, process.type);
              const isScheduled = kind === 'scheduled';
              return (
                <TableRow key={process.id} className="align-top">
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-xs">{process.type}</span>
                      <span className="text-xs text-text-muted">#{process.id}</span>
                      {process.runningSince ? (
                        <Badge variant="secondary">En curso desde {formatDate(process.runningSince)}</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={process.enabled}
                      onCheckedChange={(checked) => toggleEnabled(process, checked)}
                      aria-label={`Activar ${process.type}`}
                    />
                  </TableCell>
                  <TableCell>
                    {isScheduled ? (
                      <button
                        type="button"
                        className="cursor-pointer font-mono text-xs underline decoration-dotted"
                        onClick={() => setDialog({ mode: 'cron', process })}
                      >
                        {process.cron ?? 'sin cron'}
                      </button>
                    ) : (
                      <span className="text-xs text-text-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isScheduled ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={dryRunningId !== null}
                          title="Lee y calcula sin escribir en Lidz ni en el sistema externo"
                          onClick={() => dryRun(process)}
                        >
                          {dryRunningId === process.id ? 'Corriendo…' : 'Dry run'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!process.enabled || !!process.runningSince}
                          title={process.enabled ? undefined : 'Un proceso deshabilitado solo acepta dry run'}
                          onClick={() => setDialog({ mode: 'run', process })}
                        >
                          Real
                        </Button>
                      </div>
                    ) : kind === 'webhook' ? (
                      <span className="text-xs text-text-muted">Al recibir el webhook</span>
                    ) : (
                      <span className="text-xs text-text-muted">Tipo no registrado en esta página</span>
                    )}
                  </TableCell>
                  <TableCell className="min-w-56 max-w-80 whitespace-normal">
                    {process.lastResult ? (
                      <LastResultSummary result={process.lastResult} />
                    ) : (
                      <span className="text-xs text-text-muted">Nunca ejecutado</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(process.syncedUntil)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {dialog ? (
        <ProcessDialog
          integration={integration}
          state={dialog}
          onClose={() => setDialog(null)}
          notify={notify}
          onRunRequested={onRunRequested}
        />
      ) : null}
    </SectionBlock>
  );
};

export default ProcessesSection;
